import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, firstValueFrom, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { AuthStore } from '../../stores/auth.store';
import { AuthService } from '../services/auth.service';
import { MfaService } from '../services/mfa.service';
import { MfaOnboardingService } from '../services/mfa-onboarding.service';
import { LoggerService } from '../services/logger.service';
import { ROLES } from '@core/constants/permissions';

/**
 * Roles for which MFA is MANDATORY. Any authenticated user holding one of
 * these and who has not yet enabled MFA is force-redirected to the enrollment
 * page. Mirrors the privileged/admin tier the backend would also enforce.
 */
export const MFA_REQUIRED_ROLES: readonly string[] = [
  ROLES.SYSTEM_ADMIN,
  ROLES.TENANT_ADMIN,
];

/** The mandatory-enrollment route this guard redirects to / protects. */
export const MFA_SETUP_PATH = '/auth/mfa-setup';

/**
 * Per-session cache of the MFA-enabled status, KEYED BY user id, so repeated
 * navigations do not trigger a refetch storm against `/api/v1/auth/mfa/status`
 * on every routed page load — while a status fetched for one principal is NEVER
 * reused for another (logout → login as a different admin on the same tab). The
 * cache self-invalidates when the active user id changes; `resetMfaStatusCache()`
 * is an explicit clear hook (tests / enrollment completion).
 */
let mfaStatusCache: { userId: string; enabled: boolean } | null = null;

/**
 * Single-flight handle for an in-progress status fetch, KEYED BY user id.
 *
 * The cache above is only written AFTER the HTTP response resolves. On a hard
 * reload the router evaluates `mfaRequiredGuard` for several queued navigations
 * concurrently (bootstrap redirect → deep link), and each one would miss the
 * still-empty cache and fire its OWN `/api/v1/auth/mfa/status` request — the
 * "refetch storm after reload". This shared, replayed observable collapses all
 * concurrent misses for the same principal onto ONE request; it is cleared on
 * settle so the next genuine cache-miss cycle starts fresh. (Mirrors the
 * single-flight pattern in MfaStepUpService / AuthService refresh.)
 */
let mfaStatusInFlight: { userId: string; result$: Observable<boolean> } | null = null;

/** Test/logout hook to clear the cached MFA-enabled flag. */
export function resetMfaStatusCache(): void {
  mfaStatusCache = null;
  mfaStatusInFlight = null;
}

/** Internal: resolve MFA-enabled for `userId`, using the cache only on an id match. */
function resolveMfaEnabled(
  userId: string,
  mfaService: MfaService,
  logger: LoggerService,
): Observable<boolean> {
  if (mfaStatusCache !== null && mfaStatusCache.userId === userId) {
    return of(mfaStatusCache.enabled);
  }
  // Join an in-flight fetch for the same principal instead of starting another.
  if (mfaStatusInFlight !== null && mfaStatusInFlight.userId === userId) {
    return mfaStatusInFlight.result$;
  }
  const result$ = mfaService.getStatus().pipe(
    map((status) => {
      mfaStatusCache = { userId, enabled: status.enabled };
      return status.enabled;
    }),
    catchError((err: Error) => {
      // Fail OPEN for navigation (do not lock the user out of the whole app on
      // a transient status-endpoint error); log for visibility. The backend
      // still enforces MFA on sensitive endpoints independently. Deliberately
      // NOT cached, so a later navigation re-checks once the endpoint recovers.
      logger.warn('[mfaRequiredGuard] MFA status check failed; allowing navigation', {
        error: err.message,
      });
      return of(true);
    }),
    finalize(() => {
      // Settle: drop the shared handle so the next cache-miss cycle re-fetches.
      if (mfaStatusInFlight?.userId === userId) {
        mfaStatusInFlight = null;
      }
    }),
    // Replay the single result to every concurrent (queued) subscriber.
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  mfaStatusInFlight = { userId, result$ };
  return result$;
}

/**
 * mfaRequiredGuard — force privileged users to enroll in MFA before using the
 * app.
 *
 * Allows (does NOT redirect) when:
 *   - the user is not authenticated (authGuard owns that redirect), OR
 *   - the user holds no MFA-required role, OR
 *   - the user has already enabled MFA.
 *
 * Otherwise redirects to `/auth/mfa-setup` (and marks onboarding in progress so
 * the setup route guard permits the page). Never applied to the setup route
 * itself — wire it only on the authenticated app shell routes.
 */
export const mfaRequiredGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const mfaService = inject(MfaService);
  const onboarding = inject(MfaOnboardingService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  // Unauthenticated → defer to authGuard; do not interfere.
  if (!authService.isAuthenticated() && !authStore.isLoggedIn()) {
    return true;
  }

  // COLD-LOAD RACE FIX (mirrors roleGuard's ensureAuthHydrated): on a hard
  // refresh / deep link, AuthStore.userRoles() is [] until
  // loadEffectivePermissions() resolves. Reading roles before hydration would
  // mis-classify a privileged user as non-privileged and SKIP mandatory
  // enrollment. Await the authReady gate first (finite timeout inside).
  if (!authService.authReady()) {
    await authService.whenAuthReady();
  }

  // Non-privileged users are never forced into MFA.
  if (!authStore.hasAnyRole()(MFA_REQUIRED_ROLES as string[])) {
    return true;
  }

  const userId = authStore.currentUser()?.id;
  if (!userId) {
    // Principal id not resolvable yet → fail safe (let auth settle); backend
    // still enforces MFA on sensitive endpoints regardless.
    return true;
  }

  const enabled = await firstValueFrom(resolveMfaEnabled(userId, mfaService, logger));
  if (enabled) {
    return true;
  }
  onboarding.begin();
  return router.createUrlTree([MFA_SETUP_PATH]);
};

/**
 * mfaSetupRouteGuard — protects `/auth/mfa-setup` from arbitrary visits.
 *
 * Permits the page only when authenticated AND the session is genuinely
 * mid-enrollment: either the onboarding flag is set (post-registration or a
 * mfaRequiredGuard redirect) or the user's MFA is not yet enabled. A user who
 * has already enrolled and just types the URL is bounced to /dashboard.
 * Unauthenticated users are sent to login.
 */
export const mfaSetupRouteGuard: CanActivateFn = (): boolean | UrlTree | Observable<boolean | UrlTree> => {
  const authStore = inject(AuthStore);
  const authService = inject(AuthService);
  const mfaService = inject(MfaService);
  const onboarding = inject(MfaOnboardingService);
  const router = inject(Router);
  const logger = inject(LoggerService);

  if (!authService.isAuthenticated() && !authStore.isLoggedIn()) {
    return router.createUrlTree(['/auth/login']);
  }

  // Explicit onboarding flow always permitted.
  if (onboarding.isInProgress()) {
    return true;
  }

  const userId = authStore.currentUser()?.id;
  if (!userId) {
    // Can't determine the principal yet — allow the setup page (fail safe).
    return true;
  }

  // Otherwise only permit if MFA is genuinely not yet enabled.
  return resolveMfaEnabled(userId, mfaService, logger).pipe(
    map((enabled) => (enabled ? router.createUrlTree(['/dashboard']) : true)),
  );
};
